
import React, { useState, useEffect, useCallback, useRef } from 'react';
import useLiveQuotes from '../lib/useLiveQuotes';
import type { LiveQuote } from '../lib/useLiveQuotes';
import { coalescedGetJson } from '../lib/requestCoalesce'; // ADV-MKT-PREDEPLOY-003A A1
import MacroCalendarPanel from './MacroCalendarPanel';
import { ContinuationWatchTable } from './ContinuationWatchTable';
import { useMarketMovers, useMarketsOverview } from '../hooks/useAccountIntel';
import { 
  TrendingUp, 
  Globe, 
  Activity, 
  Search, 
  Filter, 
  ArrowUpRight, 
  ArrowDownRight,
  BarChart3,
  PieChart,
  Zap,
  Target,
  ShieldCheck,
  Layers,
  ChevronRight,
  Clock,
  Info,
  ChevronDown,
  Maximize2,
  Calendar,
  Cpu,
  Flame,
  Plus,
  Loader2,
  RefreshCw,
  ClipboardList
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { cn, logInteraction } from '../lib/utils';
import { apiV1Base } from '../lib/apiConfig';
import MarketsRefreshControl from './MarketsRefreshControl';
import { emitRefresh, subscribeRefresh } from '../lib/refreshEvents';
// 2026-06-07 n-perf-and-movers — real Watchlist + Market Pulse decision board
import WatchlistPanel from './WatchlistPanel';
import MarketPulseDecisionBoard from './MarketPulseDecisionBoard';
import RealStockChart from './RealStockChart';
import { WatchlistMembershipProvider, WatchlistAddButton } from './WatchlistAddButton';  // ADV-MKT-CHART-VIEW-WATCHLIST-001
import { consumePendingChartSymbol, clearPendingChartSymbol, clearChartQuery } from '../lib/symbolChart';  // ADV-P0K
import {
  STATUS_COLORS,
  STATUS_LABELS,
  summarizeStatus,
  formatLastUpdated,
  compactReason,
} from '../lib/marketPulseFormatters';
import { OrderTicket } from './OMSModule';
import { omsService } from '../services/omsService';

// Shared canonical Market News Status types (subset of backend contract).
type MarketNewsSectionStatus = 'LIVE' | 'STALE' | 'LIMITED' | 'UNAVAILABLE' | 'ERROR';
interface MarketNewsSection {
  status: MarketNewsSectionStatus;
  provider: string | null;
  source: string | null;
  last_updated: string | null;
  age_seconds: number | null;
  ttl_seconds: number | null;
  freshness_label: string;
  display_message: string | null;
  reason_code: string | null;
  item_count: number;
  symbols_covered: string[];
  coverage_scope: string;
  data_quality_flag: string;
  items?: Array<Record<string, unknown>>;
  economic_events?: Array<Record<string, unknown>>;
  earnings_events?: Array<Record<string, unknown>>;
  message?: string | null;
}
interface MarketNewsStatus {
  status: MarketNewsSectionStatus;
  generated_at: string;
  sections: { news: MarketNewsSection; calendar: MarketNewsSection };
  reason_codes: string[];
  display_message: string;
  providers_connected: { news: boolean; calendar: boolean };
}

// Honest UNAVAILABLE/LIMITED/STALE/LIVE badge for the new market-news contract.
function MarketNewsStatusBadge({ status }: { status: MarketNewsSectionStatus | string | null | undefined }) {
  const level = summarizeStatus(status);
  const label = (status && String(status).toUpperCase() in STATUS_LABELS)
    ? STATUS_LABELS[level]
    : STATUS_LABELS[level];
  const color = STATUS_COLORS[level];
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] font-black uppercase tracking-widest"
      style={{ borderColor: color, color }}
      data-testid="market-news-status-badge"
      data-status={String(status ?? '').toUpperCase()}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}

// Hook: fetch aggregated market news status (news + calendar) honestly.
function useMarketNewsStatus(): { data: MarketNewsStatus | null; loading: boolean; error: string | null } {
  const [data, setData] = useState<MarketNewsStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`${apiV1Base()}/market-news/status`, { cache: 'no-store' });
        if (!r.ok) {
          if (!cancelled) {
            setError(`HTTP ${r.status}`);
            setLoading(false);
          }
          return;
        }
        const body = await r.json();
        if (cancelled) return;
        const payload = body?.data ?? body;
        setData(payload as MarketNewsStatus);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(String((e as Error)?.message || e));
        setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  return { data, loading, error };
}

// Body of the "Market News Feed" card on the Macro tab. Renders honest
// state in 3 cases:
//   1. Items exist (LIVE / STALE / LIMITED): show 3-5 verified headline
//      previews with source + ET published time + linked headline.
//   2. Provider connected but no items yet: status pill already on card;
//      show the safe display_message and an explanation footer.
//   3. No provider connected: show the safe display_message + non-
//      fabrication footer.
function MarketNewsFeedBody({ news }: { news: MarketNewsSection | null }) {
  const status = (news?.status ?? 'UNAVAILABLE').toUpperCase();
  const items = (news?.items ?? []) as Array<Record<string, unknown>>;
  const itemCount = typeof news?.item_count === 'number' ? news!.item_count : items.length;
  const previews = items.slice(0, 5);
  const suppressedPaywall = Number((news as unknown as Record<string, unknown>)?.suppressed_paywall_count ?? 0);

  if (status === 'LIVE' || status === 'STALE' || (status === 'LIMITED' && previews.length > 0)) {
    const providerLabel = news?.source || news?.provider || 'verified provider';
    const freshness = status === 'LIVE' ? 'LIVE' : status === 'STALE' ? 'STALE' : 'LIMITED';
    return (
      <div className="space-y-3" data-testid="market-news-feed-items">
        <div className="space-y-2">
          {previews.map((item, i) => {
            const headline = String(item['headline'] || item['title'] || '').trim();
            if (!headline) return null;
            const url = String(item['url'] || '').trim();
            const source = String(item['source'] || item['provider'] || providerLabel).trim();
            const symbols = Array.isArray(item['symbols']) ? (item['symbols'] as unknown[]).slice(0, 4) : [];
            const published = String(item['published_at'] || '').trim();
            return (
              <div
                key={i}
                className="rounded-xl border border-[var(--input-border)] bg-[var(--input-bg)] p-3"
                data-testid="market-news-headline"
              >
                {url ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-black text-[var(--app-text)] hover:text-emerald-600"
                  >
                    {headline}
                  </a>
                ) : (
                  <div className="text-sm font-black text-[var(--app-text)]">{headline}</div>
                )}
                <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[var(--muted-text)]">
                  <span>{source}</span>
                  {published && <span>· {formatLastUpdated(published) || 'recent'}</span>}
                  {symbols.length > 0 && <span>· {symbols.join(', ')}</span>}
                </div>
              </div>
            );
          })}
        </div>
        <div
          className="rounded-xl border border-dashed border-[var(--input-border)] bg-[var(--input-bg)] p-3 text-[11px] font-bold text-[var(--muted-text)]"
          data-testid="market-news-feed-footer"
        >
          <div className="text-[var(--app-text)]">
            Showing verified headlines from {providerLabel}. Provider freshness: {freshness}.
          </div>
          {itemCount > previews.length && (
            <div className="mt-1 text-[10px] uppercase tracking-widest">
              {itemCount - previews.length} more headlines in the full feed.
            </div>
          )}
          <div className="mt-1 text-[10px] italic">
            Adversyn never fabricates headlines. Every item comes from the connected provider's feed.
          </div>
          {suppressedPaywall > 0 && (
            <div
              className="mt-2 text-[10px] uppercase tracking-widest text-amber-600"
              data-testid="market-news-paywall-note"
            >
              Some provider headlines were hidden because they may require a subscription.
            </div>
          )}
        </div>
      </div>
    );
  }

  // No items: honest unavailable/limited message.
  return (
    <div
      className="rounded-xl border border-dashed border-[var(--input-border)] bg-[var(--input-bg)] p-4 text-sm font-bold text-[var(--muted-text)]"
      data-testid="market-news-feed-status"
    >
      <div className="text-[var(--app-text)]">
        {news?.display_message || news?.message || 'No verified market news provider is connected.'}
      </div>
      {news?.reason_code && (
        <div className="mt-1 text-[10px] uppercase tracking-widest">
          Reason: {compactReason(news.reason_code)}
        </div>
      )}
      <div className="mt-1 text-[10px] italic">
        Adversyn never fabricates headlines. Real news items will appear here once a provider is connected.
      </div>
    </div>
  );
}

type MarketTab = 'overview' | 'macro' | 'flow' | 'scanner';

export default function MarketsModule() {
  const [activeTab, setActiveTab] = useState<MarketTab>('overview');
  const [selectedStock, setSelectedStock] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [scanResults, setScanResults] = useState<any[]>([]);
  const [scanError, setScanError] = useState<string | null>(null);
  const [isTradeModalOpen, setIsTradeModalOpen] = useState(false);
  const [tradeData, setTradeData] = useState<{ symbol: string, price: string } | null>(null);

  // Highlight a row when search bar selects a symbol
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { value?: string } | undefined;
      if (detail?.value) { setSelectedStock(detail.value); clearPendingChartSymbol(); }
    };
    window.addEventListener('adversyn:symbol-selected', handler);
    // ADV-P0K: on mount (e.g. after a cross-page route), open any pending/URL chart symbol.
    const _pendingSym = consumePendingChartSymbol();
    if (_pendingSym) setSelectedStock(_pendingSym);
    return () => window.removeEventListener('adversyn:symbol-selected', handler);
  }, []);

  const handleTrade = (symbol: string, price: string) => {
    logInteraction('Markets', 'Initiate Trade', { symbol, price });
    setTradeData({ symbol, price });
    setIsTradeModalOpen(true);
  };

  const handleTabChange = (tab: MarketTab) => {
    logInteraction('Markets', 'Change Tab', { tab });
    setActiveTab(tab);
  };

  const runMarketScan = async () => {
    logInteraction('Markets', 'Run Scan');
    setIsScanning(true);
    setScanError(null);
    
    try {
      // 2026-06-06 g3-no-fake-data-outside-sim-lab — Market Scanner must
      // call the real /market-pulse/movers endpoint and surface an
      // honest "Market scan unavailable" state when the call fails.
      try {
        const r = await fetch(`${apiV1Base()}/market-pulse/movers`, { cache: 'no-store' });
        if (!r.ok) {
          setScanError('Market scan unavailable.');
          setScanResults([]);
        } else {
          const j = await r.json();
          const rows = (j?.data?.combined_mover_ticker || j?.data?.top_tradable_movers || []) as any[];
          const out = rows.slice(0, 25).map((row: any) => ({
            symbol: String(row.symbol || ''),
            price: row.price != null ? String(row.price) : '—',
            change: row.change_percent != null ? `${Number(row.change_percent) >= 0 ? '+' : ''}${Number(row.change_percent).toFixed(2)}%` : '—',
            vol: row.volume != null ? String(row.volume) : '—',
            signal: row.signal || row.asset_class || '',
            _raw: row,
          }));
          setScanResults(out);
          // 2026-06-07 sitewide-refresh — broadcast so
          // MarketPulseDecisionBoard refetches its movers too.
          try {
            const evts = await import('../lib/refreshEvents');
            evts.emitRefresh('market-pulse', { source: 'run-scan' });
          } catch { /* ignore */ }
        }
      } catch (e) {
        setScanError('Market scan unavailable.');
        setScanResults([]);
      }
// Auto-switch to scanner tab if not already there to show results
      if (activeTab !== 'scanner') {
        setActiveTab('scanner');
      }
    } catch (err) {
      console.error('Scan failed:', err);
      setScanError('Market scan failed. Service unavailable.');
    } finally {
      setIsScanning(false);
    }
  };

  const renderContent = () => {
    if (selectedStock) {
      return (
        <StockDetailView 
          symbol={selectedStock} 
          onBack={() => { setSelectedStock(null); clearChartQuery(); }} 
          onTrade={handleTrade}
        />
      );
    }

    switch (activeTab) {
      case 'overview':
        return <MarketsOverview onSelectStock={setSelectedStock} onTrade={handleTrade} />;
      case 'macro':
        return <EconomicIntelligenceCenter />;
      case 'flow':
        return <InstitutionalFlowTracker />;
      case 'scanner':
        return (
          <MarketScannerEngine 
            isScanning={isScanning} 
            onScan={runMarketScan} 
            onTrade={handleTrade}
            results={scanResults} 
            error={scanError} 
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="app-container space-y-8">
      {!selectedStock && (
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <h1 className="text-4xl font-black text-[var(--app-text)] tracking-tight uppercase italic font-display">Markets</h1>
            <p className="text-[var(--muted-text)] mt-1 font-medium italic">Global multi-asset intelligence and execution analytics.</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex bg-[var(--card-bg)] border border-[var(--card-border)] rounded-xl p-1 shadow-sm">
              {[
                { id: 'overview', label: 'Overview', icon: TrendingUp },
                { id: 'macro', label: 'Macro', icon: Globe },
                { id: 'flow', label: 'Institutional Flow', icon: Activity },
                { id: 'scanner', label: 'Scanner', icon: Search },
              ].map((tab) => (
                <button
                  key={tab.id}
                  data-testid={`markets-tab-${tab.id}`}
                  onClick={() => {
                    if (activeTab === tab.id) return;
                    handleTabChange(tab.id as MarketTab);
                  }}
                  className={cn(
                    "px-4 py-2 rounded-lg text-[10px] font-black uppercase italic transition-all flex items-center gap-2 border",
                    activeTab === tab.id
                      ? "bg-[var(--app-text)] text-[var(--card-bg)] shadow-[0_0_10px_rgba(var(--app-text-rgb),0.2)] border-[var(--app-text)]"
                      : "text-[var(--muted-text)] hover:text-[var(--app-text)] hover:bg-[var(--input-border)] border-transparent"
                  )}
                >
                  <tab.icon size={14} />
                  {tab.label}
                </button>
              ))}
            </div>
            <button
              onClick={runMarketScan}
              disabled={isScanning}
              data-testid="markets-btn-run-scan"
              className={cn(
                "px-6 py-2 bg-emerald-600 text-white rounded-xl text-xs font-black uppercase italic hover:bg-emerald-700 transition-all flex items-center gap-2 shadow-lg shadow-emerald-900/20",
                isScanning && "opacity-50 cursor-not-allowed"
              )}
            >
              {isScanning ? (
                <>
                  <Loader2 className="animate-spin" size={14} />
                  Scanning...
                </>
              ) : (
                <>
                  <Zap size={14} />
                  Run Scan
                </>
              )}
            </button>
          </div>
        </div>
      )}

      <motion.div
        key={selectedStock || activeTab}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {renderContent()}
      </motion.div>

      {/* Trade Modal */}
      <AnimatePresence>
        {isTradeModalOpen && tradeData && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsTradeModalOpen(false)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              data-testid="markets-modal-trade"
              className="relative w-full max-w-lg bg-[var(--card-bg)] rounded-2xl shadow-2xl overflow-hidden"
            >
              <OrderTicket 
                initialSymbol={tradeData.symbol}
                initialPrice={tradeData.price}
                refreshOrders={() => {
                  // Since we are in MarketsModule, we don't have direct access to OMS state,
                  // but we can still call the service and maybe show a success message.
                  console.log('Order submitted from Markets page');
                }} 
                onClose={() => setIsTradeModalOpen(false)} 
              />
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MarketsOverview({ 
  onSelectStock, 
  onTrade 
}: { 
  onSelectStock: (s: string) => void,
  onTrade: (symbol: string, price: string) => void
}) {
  // 2026-06-06 g3-no-fake-data-outside-sim-lab — Markets watchlist
  // must NOT carry hardcoded AAPL/TSLA/NVDA/MSFT/AMD literals. When
  // the live market hook produces nothing, render an "unavailable"
  // state below; never inject fake quotes.
  const watchlist: Array<{ symbol: string; name: string; price: string; change: string; trend: string }> = [];

  return (
    <div className="space-y-6">
      {/* 2026-06-07 u-batch - watchlist + decision board now occupy
          the full page width. The legacy lg:col-span-2 + heatmap
          sidecar squeezed the decision board into 2/3 of the page. */}
      <div data-testid="markets-watchlist-and-decisions" className="space-y-6">
        <WatchlistPanel />
        <MarketPulseDecisionBoard />
        <ContinuationWatchTable apiBase={apiV1Base()} onSelectSymbol={onSelectStock} />
      </div>
      <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
        <MarketHeatmap />
      </div>
    </div>
  );
}

// BUGFIX 2026-06-12 — Market Heatmap restored from the working movers data path.
// Derived read-only from /api/v1/market-pulse/movers (the same source the rest of
// the Markets page uses). No fabricated sectors: tiles are grouped by lane
// (equity / etf / leveraged_long / leveraged_inverse / crypto / unknown). Honest
// empty/error states only. No banners.
const _HEAT_LANES: Array<{ key: string; label: string }> = [
  { key: 'equity', label: 'Equity' },
  { key: 'etf', label: 'ETF' },
  { key: 'leveraged_long', label: 'Leveraged Long' },
  { key: 'leveraged_inverse', label: 'Leveraged Inverse' },
  { key: 'crypto', label: 'Crypto' },
  { key: 'unknown', label: 'Unknown' },
];

function _heatNum(v: any): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(/[^0-9.\-]/g, ''));
  return Number.isFinite(n) ? n : null;
}

// ADV-MKT-PREDEPLOY-001 (Part E): identify FX/forex rows so they can be excluded
// from the equity heatmap (no FX feed on this path → no move% → dead cells).
function _isForex(row: any): boolean {
  const ac = String((row && row.asset_class) || '').toLowerCase();
  if (ac === 'forex' || ac === 'fx' || ac === 'currency') return true;
  // Defensive fallback: a 6-letter all-alpha symbol that is a known currency pair
  // shape (e.g. EURUSD, USDJPY) with no asset_class hint.
  const sym = String((row && row.symbol) || '').toUpperCase();
  return /^[A-Z]{6}$/.test(sym) && /^(USD|EUR|GBP|JPY|AUD|NZD|CAD|CHF|CNH|HKD)/.test(sym)
    && /(USD|EUR|GBP|JPY|AUD|NZD|CAD|CHF|CNH|HKD)$/.test(sym);
}

function _heatLane(row: any): string {
  const ic = String((row && row.instrument_class) || '').toLowerCase();
  if (ic === 'leveraged_long') return 'leveraged_long';
  if (ic === 'leveraged_inverse') return 'leveraged_inverse';
  const ac = String((row && row.asset_class) || '').toLowerCase();
  if (ac === 'crypto') return 'crypto';
  if (ac === 'etf') return 'etf';
  if (ac === 'us_equity' || ac === 'equity' || ac === 'stock') return 'equity';
  return 'unknown';
}

function _heatTone(move: number | null): string {
  if (move === null || !Number.isFinite(move)) return 'bg-slate-200 text-slate-500';
  if (move >= 5) return 'bg-emerald-600 text-white';
  if (move >= 2) return 'bg-emerald-500/70 text-white';
  if (move >= 0) return 'bg-emerald-500/20 text-emerald-800';
  if (move > -2) return 'bg-rose-500/20 text-rose-800';
  if (move > -5) return 'bg-rose-500/70 text-white';
  return 'bg-rose-600 text-white';
}

function _heatRing(band: string): string {
  const b = (band || '').toUpperCase();
  if (b === 'DO_NOT_TRADE') return 'ring-2 ring-rose-700';
  if (b === 'HIGH_RISK') return 'ring-2 ring-orange-500';
  if (b === 'CAUTION') return 'ring-1 ring-amber-400';
  return '';
}

function MarketHeatmap() {
  const [rows, setRows] = useState<any[] | null>(null);
  const [error, setError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const aliveRef = useRef(true);

  const load = useCallback(async (force = false) => {
    setRefreshing(true);
    try {
      // ADV-MKT-PREDEPLOY-003A (A1 dedup): coalesce identical /market-pulse/movers
      // GETs so the heatmap + decision board + run-scan share ONE in-flight /
      // recent request instead of each pulling the ~1.4MB payload separately.
      const _resp = await coalescedGetJson(`${apiV1Base()}/market-pulse/movers?view=board`, 3500, force); // ADV-MKT-PREDEPLOY-003B slim board payload // ADV-MKT-PREDEPLOY-003A: refresh bypasses cache
      if (!_resp.ok) { if (aliveRef.current) { setError(true); setRows((prev) => prev ?? []); } return; }
      const j = _resp.json;
      const d = (j && j.data) ? j.data : {};
      const pick: any[] = (d.combined_mover_ticker && d.combined_mover_ticker.length)
        ? d.combined_mover_ticker
        : (d.top_tradable_movers && d.top_tradable_movers.length)
          ? d.top_tradable_movers
          : [...(d.top_gainers || []), ...(d.top_losers || [])];
      const seen = new Set<string>();
      const out: any[] = [];
      for (const row of pick) {
        const sym = String((row && row.symbol) || "").toUpperCase();
        if (!sym || seen.has(sym)) continue;
        // ADV-MKT-PREDEPLOY-001 (Part E): exclude FX/forex pairs from the EQUITY
        // heatmap. There is no FX quote feed on this path, so they carry no move%
        // and would render as dead "—" cells in the UNKNOWN bucket. We filter them
        // out rather than fabricate data. A future FX heatmap needs a separate FX feed.
        if (_isForex(row)) continue;
        seen.add(sym);
        out.push(row);
      }
      if (aliveRef.current) { setError(false); setRows(out); setLastUpdatedAt(Date.now()); }
    } catch {
      if (aliveRef.current) { setError(true); setRows((prev) => prev ?? []); }
    } finally {
      if (aliveRef.current) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    void load(false);
    const off = subscribeRefresh("market-pulse", () => { void load(true); });
    return () => { aliveRef.current = false; off(); };
  }, [load]);

  const header = (
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-sm font-black text-[var(--app-text)] uppercase italic">Market Heatmap</h3>
      <MarketsRefreshControl
        onRefresh={() => emitRefresh("market-pulse")}
        refreshing={refreshing}
        lastUpdatedAt={lastUpdatedAt}
        testid="markets-heatmap-refresh"
      />
    </div>
  );

  let body: React.ReactNode;
  if (rows === null) {
    body = (
      <div data-testid="markets-heatmap-loading" className="rounded-xl border border-dashed border-[var(--card-border)] p-6 text-center text-xs font-bold uppercase tracking-widest text-[var(--muted-text)]">
        Loading market heatmap&hellip;
      </div>
    );
  } else if (error && rows.length === 0) {
    body = (
      <div data-testid="markets-heatmap-error" className="rounded-xl border border-dashed border-[var(--card-border)] p-6 text-center text-xs font-bold uppercase tracking-widest text-[var(--muted-text)]">
        Market heatmap unavailable &mdash; movers service error.
      </div>
    );
  } else if (!rows.length) {
    body = (
      <div data-testid="markets-heatmap-empty" className="rounded-xl border border-dashed border-[var(--card-border)] p-6 text-center text-xs font-bold uppercase tracking-widest text-[var(--muted-text)]">
        No heatmap rows available from current movers.
      </div>
    );
  } else {
    const grouped: Record<string, any[]> = {};
    for (const row of rows) {
      const lane = _heatLane(row);
      (grouped[lane] = grouped[lane] || []).push(row);
    }
    body = (
      <div data-testid="markets-heatmap" className="space-y-4">
        {_HEAT_LANES.filter((l) => (grouped[l.key] || []).length > 0).map((lane) => {
          const laneRows = (grouped[lane.key] || []).slice().sort(
            (a, b) => (_heatNum(b.change_percent) || 0) - (_heatNum(a.change_percent) || 0),
          );
          return (
            <div key={lane.key} data-testid={`markets-heatmap-group-${lane.key}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)]">{lane.label}</span>
                <span className="text-[10px] font-bold text-[var(--muted-text)]">({laneRows.length})</span>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-8 gap-1.5">
                {laneRows.map((row, i) => {
                  const sym = String(row.symbol || "").toUpperCase();
                  const move = _heatNum(row.change_percent);
                  const band = String(row.integrity_band || "");
                  const bits: string[] = [];
                  if (move !== null) bits.push(`${move >= 0 ? "+" : ""}${move.toFixed(2)}%`);
                  if (row.signal_use_display) bits.push(String(row.signal_use_display));
                  if (band) bits.push(`Integrity ${band}`);
                  if (row.catalyst_status) bits.push(String(row.catalyst_status));
                  return (
                    <div key={sym + i} data-testid="markets-heatmap-tile" data-symbol={sym} data-integrity-band={band}
                         title={bits.join(" · ")}
                         role="button" tabIndex={0} onClick={() => { try { window.dispatchEvent(new CustomEvent("adversyn:symbol-selected", { detail: { value: sym } })); } catch { /* ignore */ } }} className={`relative group rounded-md px-2 py-1.5 text-center cursor-pointer ${_heatTone(move)} ${_heatRing(band)}`}>
                      <button
                        type="button"
                        data-testid="heatmap-plan-action"
                        title="Plan paper session"
                        onClick={(e) => { e.stopPropagation(); try { window.dispatchEvent(new CustomEvent("adversyn:plan-paper-session", { detail: row })); } catch { /* ignore */ } }}
                        className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 transition-opacity rounded bg-[var(--card-bg)]/80 p-0.5 text-[var(--muted-text)] hover:text-[var(--app-text)]"
                      >
                        <ClipboardList size={10} />
                      </button>
                      <div className="text-[11px] font-black leading-none truncate">{sym}</div>
                      <div className="text-[10px] font-bold leading-none mt-0.5">{move === null ? "—" : `${move >= 0 ? "+" : ""}${move.toFixed(1)}%`}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (<>{header}{body}</>);
}
function StockDetailView({ 
  symbol, 
  onBack,
  onTrade
}: { 
  symbol: string, 
  onBack: () => void,
  onTrade: (symbol: string, price: string) => void
}) {
  const [activeTab, setActiveTab] = useState<'chart' | 'level2' | 'options' | 'liquidity'>('chart');
  const [chartTimeframe, setChartTimeframe] = useState('1D');
  // 2026-06-07 s-batch — real quote service
  const liveQuotes = useLiveQuotes([symbol]);
  const liveQuote = liveQuotes.quotes[symbol] || null;
  const quotePrice = liveQuote && typeof liveQuote.price === 'number'
    ? liveQuote.price
    : null;
  const tradeBtnPrice = quotePrice !== null ? String(quotePrice.toFixed(2)) : '';
  const dataQuality = liveQuote?.data_quality ?? 'unavailable';
  const tradeDisabled = !tradeBtnPrice || dataQuality === 'unavailable';
  // PRICERECON-001 — cross-path reconciliation verdict (computed in RealStockChart from
  // bars + levels + this header quote), surfaced up for an honest banner + header marker.
  const [reconcile, setReconcile] = useState<import('../lib/priceReconcile').ReconcileResult | null>(null);
  const unreconciled = reconcile?.verdict === 'UNRECONCILED';

  const handleTabChange = (tab: any) => {
    logInteraction('StockDetail', 'Change Tab', { tab });
    setActiveTab(tab);
  };

  const handleTimeframeChange = (tf: string) => {
    logInteraction('StockDetail', 'Change Timeframe', { tf });
    setChartTimeframe(tf);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          data-testid="markets-btn-back"
          className="flex items-center gap-2 text-xs font-black text-[var(--muted-text)] uppercase italic hover:text-[var(--app-text)] transition-colors"
        >
          <ChevronRight className="rotate-180" size={16} /> Back to Markets
        </button>
        <div className="flex items-center gap-4">
          <button
            data-testid="symbol-detail-trade"
            onClick={() => { if (!tradeDisabled) onTrade(symbol, tradeBtnPrice); }}
            disabled={tradeDisabled}
            title={tradeDisabled ? 'Quote unavailable - cannot trade without a live price' : ''}
            className="px-6 py-2 bg-emerald-600 text-white rounded-xl text-xs font-black uppercase italic hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Trade {symbol}
          </button>
          <button
            type="button"
            data-testid="symbol-detail-refresh-quote"
            onClick={() => { void liveQuotes.refresh(); }}
            disabled={liveQuotes.loading}
            title="Refresh quote"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest border border-[var(--card-border)] bg-[var(--input-bg)] hover:bg-[var(--card-border)]/40 disabled:opacity-50"
          >
            <RefreshCw size={12} className={liveQuotes.loading ? 'animate-spin' : ''} />
            {liveQuotes.loading ? 'Refreshing…' : 'Refresh Quote'}
          </button>
          <div className="text-right">
            <h2 data-testid="symbol-detail-symbol" className="text-2xl font-black text-[var(--app-text)] uppercase italic">{symbol}</h2>
            <div className="flex items-center justify-end gap-2 text-xs font-bold uppercase tracking-widest">
              <span data-testid="symbol-detail-price" className={'font-mono ' + (unreconciled ? 'text-[var(--muted-text)] line-through' : 'text-[var(--app-text)]')}>
                {quotePrice !== null ? '$' + quotePrice.toFixed(2) : '—'}
              </span>
              {unreconciled && (
                <span data-testid="symbol-detail-price-unverified" className="text-[9px] font-black uppercase tracking-widest text-rose-600 dark:text-rose-400">Unverified</span>
              )}
              <span data-testid="symbol-detail-freshness" data-quote-quality={dataQuality}
                className={
                  'inline-block px-2 py-0.5 rounded border text-[10px] ' +
                  (dataQuality === 'live' ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30' :
                   dataQuality === 'stale' ? 'bg-amber-500/10 text-amber-700 border-amber-500/30' :
                   dataQuality === 'after_hours' ? 'bg-slate-500/10 text-slate-700 border-slate-500/30' :
                   dataQuality === 'closed' ? 'bg-slate-500/10 text-slate-700 border-slate-500/30' :
                   'bg-slate-500/10 text-slate-500 border-slate-500/30')
                }>
                {dataQuality.toUpperCase().replace('_', ' ')}
              </span>
            </div>
            <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
              Source: {liveQuote?.source || '—'}
            </div>
          </div>
          <div className="w-12 h-12 bg-[var(--app-text)] rounded-xl flex items-center justify-center text-[var(--card-bg)] font-black italic">
            {symbol[0]}
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex bg-[var(--card-bg)] border border-[var(--card-border)] rounded-xl p-1 shadow-sm w-fit">
          {[
            { id: 'chart', label: 'Technical Chart', icon: TrendingUp },
            { id: 'level2', label: 'Level II Depth', icon: Layers },
            { id: 'options', label: 'Options Center', icon: Target },
            { id: 'liquidity', label: 'Liquidity Analyzer', icon: Flame },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id as any)}
              className={cn(
                "px-4 py-2 rounded-lg text-[10px] font-black uppercase italic transition-all flex items-center gap-2",
                activeTab === tab.id ? "bg-[var(--app-text)] text-[var(--card-bg)] shadow-sm" : "text-[var(--muted-text)] hover:text-[var(--app-text)]"
              )}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
          {/* ADV-MKT-CHART-VIEW-WATCHLIST-001 — one-click Watchlist action, placed ONLY
              here in the technical toolbar (never in the Trade/Refresh top row). Reuses the
              Markets board watchlist button + backend + watchlist-changed event; the local
              membership provider lets an already-saved symbol render "In Watchlist". */}
          <div className="w-px self-stretch bg-[var(--card-border)] mx-1" aria-hidden="true" />
          <WatchlistMembershipProvider>
            <WatchlistAddButton symbol={symbol} variant="toolbar" />
          </WatchlistMembershipProvider>
        </div>

        {activeTab === 'chart' && (
          <div className="flex bg-[var(--card-bg)] border border-[var(--card-border)] rounded-xl p-1 shadow-sm">
            {['1D', '1W', '1M', '1Y'].map((tf) => (
              <button
                key={tf}
                onClick={() => handleTimeframeChange(tf)}
                className={cn(
                  "px-4 py-1.5 rounded-lg text-[10px] font-black uppercase italic transition-all",
                  chartTimeframe === tf ? "bg-[var(--app-text)] text-[var(--card-bg)] shadow-sm" : "text-[var(--muted-text)] hover:text-[var(--app-text)]"
                )}
              >
                {tf}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* PRICERECON-001 — fail-closed price banner: when the header / candles / tooltip / C4 levels
          disagree beyond tolerance (e.g. a raw series spanning a split, or a stale snapshot), show an
          HONEST unreconciled/stale state instead of a confident wrong number. No price is fabricated
          or averaged; integrity/decision_band/gating logic is untouched. */}
      {reconcile && reconcile.verdict !== 'RECONCILED' && (
        <div data-testid="price-reconcile-banner" data-verdict={reconcile.verdict}
          className={'rounded-xl border px-4 py-3 ' + (reconcile.verdict === 'UNRECONCILED'
            ? 'border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300'
            : 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300')}>
          <div className="text-[11px] font-black uppercase tracking-widest">
            {reconcile.verdict === 'UNRECONCILED'
              ? 'Price unreconciled — sources disagree; verify externally'
              : 'Stale quote — may not reflect the current price'}
          </div>
          {reconcile.reasons.length > 0 && (
            <ul className="mt-1 space-y-0.5 text-[10px] font-medium leading-relaxed list-disc list-inside">
              {reconcile.reasons.map((r, i) => (<li key={i}>{r}</li>))}
            </ul>
          )}
        </div>
      )}

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab + chartTimeframe}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === 'chart' && (
            <RealStockChart symbol={symbol} timeframe={chartTimeframe}
              headerPrice={quotePrice} headerStale={dataQuality === 'stale'} headerQuality={dataQuality}
              onReconcile={setReconcile} />
          )}
          {activeTab === 'level2' && <LevelIIMarketDepth quote={liveQuote} />}
          {activeTab === 'options' && <OptionsAnalyticsCenter />}
          {activeTab === 'liquidity' && <LiquidityAnalyzer quote={liveQuote} />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function LevelIIMarketDepth({ quote }: { quote: LiveQuote | null }) {
  // 2026-06-13 ui-markets-card-refresh — real compact liquidity snapshot from
  // the live quote (bid/ask/spread/volume). Honest empty when no quote data.
  const liqBid = quote && typeof quote.bid === 'number' ? quote.bid : null;
  const liqAsk = quote && typeof quote.ask === 'number' ? quote.ask : null;
  const liqVol = quote && typeof quote.volume === 'number' ? quote.volume : null;
  const liqMid = (liqBid !== null && liqAsk !== null) ? (liqBid + liqAsk) / 2 : (quote && typeof quote.price === 'number' ? quote.price : null);
  const liqSpread = (liqBid !== null && liqAsk !== null) ? (liqAsk - liqBid) : null;
  const liqSpreadBps = (liqSpread !== null && liqMid && liqMid > 0) ? (liqSpread / liqMid) * 10000 : null;
  const liqHasData = liqBid !== null || liqAsk !== null || liqVol !== null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <div className="lg:col-span-8 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-sm">
        <div className="p-4 border-b border-[var(--card-border)]">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Top of Book</h4>
            <span className="text-[9px] font-bold uppercase tracking-widest text-[var(--muted-text)]">Full Level II depth unavailable</span>
          </div>
          {(liqBid !== null || liqAsk !== null) ? (
            <div data-testid="markets-level2-top-of-book" className="grid grid-cols-3 gap-3 text-center">
              <div>
                <div className="text-[9px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Bid</div>
                <div className="text-sm font-black text-emerald-600 italic font-mono">{liqBid !== null ? '$' + liqBid.toFixed(2) : '\u2014'}</div>
              </div>
              <div>
                <div className="text-[9px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Ask</div>
                <div className="text-sm font-black text-rose-600 italic font-mono">{liqAsk !== null ? '$' + liqAsk.toFixed(2) : '\u2014'}</div>
              </div>
              <div>
                <div className="text-[9px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Spread</div>
                <div className="text-sm font-black text-[var(--app-text)] italic font-mono">{liqSpread !== null ? '$' + liqSpread.toFixed(2) : '\u2014'}{liqSpreadBps !== null ? <span className="text-[9px] text-[var(--muted-text)]"> ({liqSpreadBps.toFixed(0)} bps)</span> : null}</div>
              </div>
              <div className="col-span-3 mt-1 text-[9px] font-bold uppercase tracking-widest text-[var(--muted-text)]">Source: {quote?.source || '\u2014'} \u00b7 {String(quote?.data_quality || 'unavailable').toUpperCase().replace('_', ' ')}</div>
            </div>
          ) : (
            <div data-testid="markets-level2-unavailable" className="rounded-xl border border-dashed border-[var(--card-border)] p-6 text-center text-xs font-bold uppercase tracking-widest text-[var(--muted-text)]">
              Level II depth unavailable for this symbol.
            </div>
          )}
        </div>
        <div className="p-4 bg-[var(--card-header-bg)]">
          <h4 className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-4">Liquidity Heatmap</h4>
          {liqHasData ? (
            <div data-testid="markets-liquidity-heatmap" className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="text-center">
                  <div className="text-[9px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Bid</div>
                  <div className="text-sm font-black text-emerald-600 italic font-mono">{liqBid !== null ? '$' + liqBid.toFixed(2) : '—'}</div>
                </div>
                <div className="text-center">
                  <div className="text-[9px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Ask</div>
                  <div className="text-sm font-black text-rose-600 italic font-mono">{liqAsk !== null ? '$' + liqAsk.toFixed(2) : '—'}</div>
                </div>
                <div className="text-center">
                  <div className="text-[9px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Spread</div>
                  <div className="text-sm font-black text-[var(--app-text)] italic font-mono">{liqSpread !== null ? '$' + liqSpread.toFixed(2) : '—'}{liqSpreadBps !== null ? <span className="text-[9px] text-[var(--muted-text)]"> ({liqSpreadBps.toFixed(0)} bps)</span> : null}</div>
                </div>
                <div className="text-center">
                  <div className="text-[9px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Volume</div>
                  <div className="text-sm font-black text-[var(--app-text)] italic font-mono">{liqVol !== null ? liqVol.toLocaleString() : '—'}</div>
                </div>
              </div>
              {liqSpreadBps !== null && (
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 rounded-full bg-[var(--input-bg)] overflow-hidden">
                    <div className={`h-full ${liqSpreadBps <= 25 ? 'bg-emerald-500' : liqSpreadBps <= 100 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{ width: `${Math.max(4, Math.min(100, 100 - Math.min(liqSpreadBps, 200) / 2))}%` }} />
                  </div>
                  <span className="text-[9px] font-bold uppercase tracking-widest text-[var(--muted-text)]">{liqSpreadBps <= 25 ? 'Tight' : liqSpreadBps <= 100 ? 'Moderate' : 'Wide'} spread</span>
                </div>
              )}
              <div className="text-[9px] font-bold uppercase tracking-widest text-[var(--muted-text)]">
                Source: {quote?.source || '—'} · {String(quote?.data_quality || 'unavailable').toUpperCase().replace('_', ' ')}
              </div>
            </div>
          ) : (
            <div data-testid="markets-liquidity-heatmap-unavailable" className="h-32 rounded-xl border border-dashed border-[var(--card-border)] flex items-center justify-center text-xs font-bold uppercase tracking-widest text-[var(--muted-text)] text-center px-4">
              Liquidity heatmap data unavailable for this symbol.
            </div>
          )}
        </div>
      </div>

      <div className="lg:col-span-4 space-y-6">
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
          <h4 className="text-xs font-black text-[var(--app-text)] uppercase italic mb-4">Time & Sales</h4>
          <div data-testid="markets-time-and-sales-unavailable" className="rounded-xl border border-dashed border-[var(--card-border)] p-8 text-center text-xs font-bold uppercase tracking-widest text-[var(--muted-text)]">
            Time & Sales unavailable.
          </div>
        </div>
      </div>
    </div>
  );
}

function OptionsAnalyticsCenter() {
  // 2026-06-13 stock-detail sample-data removal: there is no real options data source
  // (no greeks / chain / IV backend). Show an honest compact empty state instead of
  // fabricated greeks + a synthetic options chain.
  return (
    <div className="space-y-6">
      <div data-testid="markets-options-unavailable" className="bg-[var(--card-bg)] border border-dashed border-[var(--card-border)] rounded-2xl p-8 text-center text-xs font-bold uppercase tracking-widest text-[var(--muted-text)]">
        Options analytics unavailable for this symbol.
      </div>
    </div>
  );
}
function LiquidityAnalyzer({ quote }: { quote: LiveQuote | null }) {
  // 2026-06-13 stock-detail sample-data removal: derive ONLY from the real quote.
  // Order imbalance / depth notional / a 0-10 liquidity score require a Level II feed
  // that is not available, so those fabricated values are removed.
  const bid = quote && typeof quote.bid === 'number' ? quote.bid : null;
  const ask = quote && typeof quote.ask === 'number' ? quote.ask : null;
  const vol = quote && typeof quote.volume === 'number' ? quote.volume : null;
  const mid = (bid !== null && ask !== null) ? (bid + ask) / 2 : (quote && typeof quote.price === 'number' ? quote.price : null);
  const spread = (bid !== null && ask !== null) ? (ask - bid) : null;
  const bps = (spread !== null && mid && mid > 0) ? (spread / mid) * 10000 : null;
  const hasData = bid !== null || ask !== null || vol !== null;
  const tightness = bps === null ? null : bps <= 25 ? 'Tight' : bps <= 100 ? 'Moderate' : 'Wide';
  const toneCls = bps === null ? 'text-[var(--muted-text)]' : bps <= 25 ? 'text-emerald-600' : bps <= 100 ? 'text-amber-600' : 'text-rose-600';
  if (!hasData) {
    return (
      <div className="space-y-6">
        <div data-testid="markets-liquidity-analyzer-unavailable" className="bg-[var(--card-bg)] border border-dashed border-[var(--card-border)] rounded-2xl p-8 text-center text-xs font-bold uppercase tracking-widest text-[var(--muted-text)]">
          Liquidity analysis unavailable for this symbol.
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-6">
      <div data-testid="markets-liquidity-analyzer" className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-xs font-black text-[var(--app-text)] uppercase italic">Liquidity (derived from quote)</h4>
          {tightness && <span className={`text-[10px] font-black uppercase tracking-widest ${toneCls}`}>{tightness} spread</span>}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center"><div className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Bid</div><div className="text-lg font-black text-emerald-600 italic font-mono">{bid !== null ? '$' + bid.toFixed(2) : '\u2014'}</div></div>
          <div className="text-center"><div className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Ask</div><div className="text-lg font-black text-rose-600 italic font-mono">{ask !== null ? '$' + ask.toFixed(2) : '\u2014'}</div></div>
          <div className="text-center"><div className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Spread</div><div className="text-lg font-black text-[var(--app-text)] italic font-mono">{spread !== null ? '$' + spread.toFixed(2) : '\u2014'}{bps !== null ? ' / ' + bps.toFixed(0) + ' bps' : ''}</div></div>
          <div className="text-center"><div className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Volume</div><div className="text-lg font-black text-[var(--app-text)] italic font-mono">{vol !== null ? vol.toLocaleString() : '\u2014'}</div></div>
        </div>
        <div className="mt-3 text-[9px] font-bold uppercase tracking-widest text-[var(--muted-text)]">Source: {quote?.source || '\u2014'} \u00b7 {String(quote?.data_quality || 'unavailable').toUpperCase().replace('_', ' ')} \u00b7 Order imbalance / depth require a Level II feed (unavailable).</div>
      </div>
    </div>
  );
}
function EconomicIntelligenceCenter() {
  // 2026-06-07 r-batch - replaced legacy blank-dash calendar block
  // with MacroCalendarPanel (summary bar + filter chips + refresh +
  // honest empty state). Wraps /api/v1/market/macro-calendar.
  return (
    <div className="space-y-6" data-testid="economic-intel-center">
      <MacroCalendarPanel />
    </div>
  );
}

function InstitutionalFlowTracker() {
  return (
    <div className="space-y-8" data-testid="institutional-flow-tracker">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
          <div className="flex items-start justify-between gap-4 mb-4">
            <h3 className="text-lg font-black text-[var(--app-text)] uppercase italic flex items-center gap-2">
              <Layers size={20} className="text-emerald-600" /> Dark Pool Activity
            </h3>
            <MarketNewsStatusBadge status="UNAVAILABLE" />
          </div>
          <div
            className="rounded-xl border border-dashed border-[var(--input-border)] bg-[var(--input-bg)] p-4 text-sm font-bold text-[var(--muted-text)]"
            data-testid="dark-pool-unavailable"
          >
            <div className="text-[var(--app-text)]">
              No verified dark-pool / institutional block-trade provider is connected.
            </div>
            <div className="mt-1 text-[10px] uppercase tracking-widest">
              Reason: {compactReason('PROVIDER_UNAVAILABLE')}
            </div>
            <div className="mt-1 text-[10px] italic">
              Block trades and dark-pool prints are not shown here unless they come from a live, verified source.
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
            <div className="flex items-start justify-between gap-4 mb-4">
              <h3 className="text-sm font-black text-[var(--app-text)] uppercase italic">Accumulation Signals</h3>
              <MarketNewsStatusBadge status="UNAVAILABLE" />
            </div>
            <div
              className="rounded-xl border border-dashed border-[var(--input-border)] bg-[var(--input-bg)] p-4 text-sm font-bold text-[var(--muted-text)]"
              data-testid="accumulation-signals-unavailable"
            >
              <div className="text-[var(--app-text)]">
                No verified institutional accumulation / distribution signal provider is connected.
              </div>
              <div className="mt-1 text-[10px] uppercase tracking-widest">
                Reason: {compactReason('PROVIDER_UNAVAILABLE')}
              </div>
              <div className="mt-1 text-[10px] italic">
                Adversyn does not infer institutional flow from price action alone. No fabricated confidence scores are shown.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MarketScannerEngine({ 
  isScanning, 
  onScan, 
  onTrade,
  results, 
  error 
}: { 
  isScanning: boolean, 
  onScan: () => void, 
  onTrade: (symbol: string, price: string) => void,
  results: any[], 
  error: string | null 
}) {
  const [filters, setFilters] = useState({
    movement: 'Gainers > 5%',
    volume: '> 2x Avg Vol',
    cap: 'Large Cap (> $10B)',
    signal: 'RSI Oversold'
  });

  const handleFilterChange = (key: string, value: string) => {
    logInteraction('Scanner', 'Change Filter', { key, value });
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-8">
      <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-black text-[var(--app-text)] uppercase italic">Real-Time Scanner Filters</h3>
          <button 
            onClick={onScan}
            disabled={isScanning}
            className={cn(
              "px-4 py-2 bg-emerald-600 text-white rounded-xl text-xs font-black uppercase italic hover:bg-emerald-700 transition-all flex items-center gap-2",
              isScanning && "opacity-50 cursor-not-allowed"
            )}
          >
            {isScanning ? <Loader2 className="animate-spin" size={14} /> : <Zap size={14} />}
            {isScanning ? 'Scanning...' : 'Run Scan'}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="space-y-2">
            <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Price Movement</label>
            <select
              data-testid="scanner-select-movement"
              value={filters.movement}
              onChange={(e) => handleFilterChange('movement', e.target.value)}
              className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2 text-sm font-black text-[var(--app-text)] outline-none"
            >
              <option>Gainers &gt; 5%</option>
              <option>Losers &gt; 5%</option>
              <option>Gap Ups &gt; 2%</option>
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Volume Ratio</label>
            <select
              data-testid="scanner-select-volume"
              value={filters.volume}
              onChange={(e) => handleFilterChange('volume', e.target.value)}
              className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2 text-sm font-black text-[var(--app-text)] outline-none"
            >
              <option>&gt; 2x Avg Vol</option>
              <option>&gt; 5x Avg Vol</option>
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Market Cap</label>
            <select
              data-testid="scanner-select-cap"
              value={filters.cap}
              onChange={(e) => handleFilterChange('cap', e.target.value)}
              className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2 text-sm font-black text-[var(--app-text)] outline-none"
            >
              <option>Large Cap (&gt; $10B)</option>
              <option>Mid Cap ($2B - $10B)</option>
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Technical Signal</label>
            <select
              data-testid="scanner-select-signal"
              value={filters.signal}
              onChange={(e) => handleFilterChange('signal', e.target.value)}
              className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2 text-sm font-black text-[var(--app-text)] outline-none"
            >
              <option>RSI Oversold</option>
              <option>Golden Cross</option>
              <option>MACD Crossover</option>
            </select>
          </div>
        </div>
      </div>

      <div data-testid="scanner-panel-results" className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-sm">
        {error && (
          <div className="p-4 bg-rose-500/10 border-b border-rose-500/20 text-rose-600 text-xs font-black uppercase italic flex items-center gap-2">
            <Info size={14} />
            {error}
          </div>
        )}
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-[var(--card-header-bg)] border-b border-[var(--card-header-border)]">
              <th className="px-6 py-3 text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Symbol</th>
              <th className="px-6 py-3 text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Price</th>
              <th className="px-6 py-3 text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Change</th>
              <th className="px-6 py-3 text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Volume</th>
              <th className="px-6 py-3 text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Signal</th>
              <th className="px-6 py-3 text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Action</th>
            </tr>
          </thead>
          <tbody>
            {isScanning ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center gap-4">
                    <Loader2 className="animate-spin text-emerald-600" size={32} />
                    <div className="text-xs font-black text-[var(--muted-text)] uppercase tracking-widest animate-pulse">Executing Market Scan...</div>
                  </div>
                </td>
              </tr>
            ) : results.length > 0 ? (
              results.map((res) => (
                <tr key={res.symbol} className="border-b border-[var(--card-border)] hover:bg-[var(--input-bg)] transition-colors">
                  <td className="px-6 py-4 text-sm font-black text-[var(--app-text)] italic">
                    <div className="flex items-center gap-2">
                      <button type="button" data-testid="scanner-symbol-open" data-symbol={res.symbol}
                        onClick={() => { try { window.dispatchEvent(new CustomEvent("adversyn:symbol-selected", { detail: { value: res.symbol } })); } catch { /* ignore */ } }}
                        className="hover:text-emerald-600 transition-colors cursor-pointer">{res.symbol}</button>
                      <button
                        type="button"
                        data-testid="scanner-plan-action"
                        data-symbol={res.symbol}
                        title="Plan paper session"
                        onClick={() => { try { const x = parseFloat(String(res.price)); const snap = res._raw ? { ...res._raw, __source_component: "scanner" } : { symbol: res.symbol, price: Number.isFinite(x) ? x : null, __source_component: "scanner" }; window.dispatchEvent(new CustomEvent("adversyn:plan-paper-session", { detail: snap })); } catch { /* ignore */ } }}
                        className="inline-flex items-center justify-center h-5 w-5 rounded border border-[var(--card-border)] text-[var(--muted-text)] hover:text-[var(--app-text)] hover:bg-[var(--input-bg)]"
                      >
                        <ClipboardList size={11} />
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-bold text-[var(--app-text)]">${res.price}</td>
                  <td className="px-6 py-4 text-sm font-bold text-emerald-600">{res.change}</td>
                  <td className="px-6 py-4 text-sm font-bold text-[var(--muted-text)]">{res.vol}</td>
                  <td className="px-6 py-4 text-[10px] font-black text-emerald-600 uppercase italic">{res.signal}</td>
                  <td className="px-6 py-4">
                    <button 
                      onClick={() => onTrade(res.symbol, res.price)}
                      className="text-xs font-black text-[var(--app-text)] uppercase italic hover:text-emerald-600 transition-colors"
                    >
                      Trade
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              /* 2026-06-06 h-source-of-truth-purge — second SMCI/ARM/COIN
                 fallback block removed. When no live results, show the
                 honest empty state. */
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-xs font-black text-[var(--muted-text)] uppercase tracking-widest" data-testid="markets-scanner-empty">
                  Market scan unavailable — try again after Market Pulse refresh.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
